import React, { useState, useEffect, useMemo, useCallback } from 'react';

import _ from 'lodash';
import classNames from 'classnames';

import { Search } from 'lucide-react';
import { TokensTable } from './tokens-table';
import { SelectedTokensList } from './tokens-list';
import useTokenOptionsStore from '@/stores/TokenOptionsStore';

import { MAX_TOKENS } from '@/constants/tokens';
import { RaceData, Token } from '@/pages/race-wizard';

const SelectTokensView = ({ onNext, tokens = [] }: { onNext: (data: Partial<RaceData>) => void; tokens: Token[] }) => {
  const {
    cryptos: filteredTokens,
    categories,
    fetchData,
    loadMore,
    loading,
    activeCategory,
    setActiveCategory,
    inputSearch,
    setInputSearch,
    searchToken,
    resetPage,
    page,
  } = useTokenOptionsStore();

  const [selectedTokens, setSelectedTokens] = useState(tokens);
  const hasMoreTokens = useMemo(() => filteredTokens.length >= page * 10, [filteredTokens, page]);

  const handleLoadMore = useCallback(() => loadMore(), [loadMore]);

  const handleNext = useCallback(() => {
    onNext({ selectedTokens });
  }, [onNext, selectedTokens]);

  const handleCategoryChange = useCallback(
    async (value: string) => {
      setActiveCategory(value);
      setSelectedTokens([]);
      resetPage();
      if (inputSearch) {
        searchToken();
      } else {
        fetchData();
      }
    },
    [activeCategory, categories, fetchData, inputSearch, resetPage, searchToken, setActiveCategory, setSelectedTokens]
  );

  const debouncedSearch = useCallback(
    _.debounce(async _ => {
      try {
        resetPage();
        searchToken();
      } catch (error) {
        console.error('Search failed:', error);
      }
    }, 500),
    [activeCategory]
  );

  const handleSearchInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputSearch(value);
      debouncedSearch(value);
    },
    [debouncedSearch, setInputSearch]
  );

  const handleTokenSelect = useCallback((token: Token) => {
    setSelectedTokens(prev => {
      const isSelected = prev.some(t => t.id === token.id);
      if (isSelected) return prev.filter(t => t.id !== token.id);
      if (prev.length >= MAX_TOKENS) return prev;
      return [...prev, token];
    });
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <React.Fragment>
      <div className="-mx-10 flex items-center gap-2 overflow-x-auto px-10 pb-4 text-xs lg:text-sm">
        {categories.map(category => {
          const isActive = activeCategory === category.value;

          return (
            <button
              disabled={loading}
              key={category.id}
              onClick={() => handleCategoryChange(category.value)}
              className={classNames(
                'shrink-0 cursor-pointer touch-manipulation select-none rounded-full px-4 py-1.5 transition-all duration-200 active:scale-95',
                {
                  'bg-[#F2F2F7] text-[#31111D]': isActive,
                  'border border-current bg-[#10091C] text-[#AEAEB2] hover:bg-[#7c3aed33]': !isActive,
                }
              )}
            >
              {category.label}
            </button>
          );
        })}
      </div>

      <div className="relative !mt-4 flex items-center rounded-lg border border-[#282133] bg-[#090409]">
        <input
          type="text"
          value={inputSearch}
          className="px-4 py-2 placeholder:text-[#686868]"
          onChange={handleSearchInput}
          placeholder="Choose your token"
        />
        <Search className="absolute right-3 size-4 stroke-[#686868]" size={20} />
      </div>

      <div
        // display: grid here prevents overflow on tablet screens
        className="-mx-10 grid px-10 text-sm"
      >
        <div className="h-60 w-full overflow-auto">
          <TokensTable
            isLoading={loading}
            tokens={filteredTokens}
            selectedTokens={selectedTokens}
            onTokenSelect={handleTokenSelect}
          />
        </div>

        {!loading && !inputSearch && hasMoreTokens && (
          <div className="flex w-full justify-center pt-3">
            <button
              className="cursor-pointer !rounded-3xl border border-solid border-white bg-transparent px-4 py-2 !text-sm text-white transition-colors duration-300 hover:bg-white hover:text-black"
              onClick={handleLoadMore}
              aria-label="Load more tokens"
            >
              Load more
            </button>
          </div>
        )}
      </div>

      <SelectedTokensList tokens={selectedTokens} handleTokenSelect={handleTokenSelect} />

      <div>
        <button
          onClick={handleNext}
          disabled={selectedTokens.length === 0}
          className="min-w-40 rounded-lg bg-[#9937F7] p-2 font-bold"
        >
          Continue
        </button>
      </div>
    </React.Fragment>
  );
};

export default SelectTokensView;
